import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { DeductionDetailSection } from '@app/enums/deduction-detail-section.enum';
import { TableConfig } from '@app/interfaces';
import { DeductionEntity, DeductionGradingEntity } from '@app/shared/models/deduction';
import { TableComponent } from '../../components/table/table.component';
import { DecimalFormatDirective } from '../../directives/decimal-format.directive';
import { NumbersOnlyDirective } from '../../directives/numbers-only.directive';

@Component({
    selector: 'pr-grading-table',
    template: `
		<div class="table-container">
			<div class="center-content">
				<mat-checkbox class="linq-mx-0 linq-my-0 w-25" [(ngModel)]="gradingByFy" (change)="onGradingByFY()">By Fiscal Year</mat-checkbox>
				<button class="linq-mb-8" mat-button defaultColor="primary" color="primary" (click)="addNewDeductionGrade()">
					<mat-icon>add</mat-icon>
					ADD ROW
				</button>
			</div>
			<sl-table [data]="deductionGrade" [config]="config" [newRow]="newRow"></sl-table>
		</div>
		<ng-template #min let-row="row" let-column="column" let-fc="fc">
			<mat-form-field color="accent">
				<input
					matInput
					[(ngModel)]="row[column.key]"
					(change)="newValue()"
					(paste)="preventPaste($event, fc, column, row)"
					[formControl]="fc"
					placeholder="0.00"
					inputmode="numeric"
					decimalFormat
					[allowDecimals]="true"
					[limitTo]="limitTo.amount"
					numbersOnly
				/>
			</mat-form-field>
		</ng-template>
		<ng-template #amount let-row="row" let-column="column" let-fc="fc">
			<mat-form-field color="accent">
				<input
					matInput
					[(ngModel)]="row[column.key]"
					(change)="newValue()"
					(paste)="preventPaste($event, fc, column, row)"
					[disabled]="row.Percentage > 0"
					placeholder="0.00"
					inputmode="numeric"
					decimalFormat
					[allowDecimals]="true"
					[limitTo]="limitTo.amount"
					numbersOnly
				/>
			</mat-form-field>
		</ng-template>
		<ng-template #percentage let-row="row" let-column="column" let-fc="fc">
			<mat-form-field color="accent">
				<input
					matInput
					[(ngModel)]="row[column.key]"
					(change)="newValue()"
					(paste)="preventPaste($event, fc, column, row)"
					[disabled]="row.Amount > 0"
					placeholder="0.00"
					inputmode="numeric"
					decimalFormat
					[allowDecimals]="true"
					[limitTo]="limitTo.percentage"
					numbersOnly
				/>
			</mat-form-field>
		</ng-template>
		<ng-template #delete let-row="row" let-column="column" let-fc="fc">
			<div class="auto-size text-center">
				<button mat-button defaultColor="primary" color="primary" (click)="deleteGrade(row)">
					<mat-icon>delete</mat-icon>
				</button>
			</div>
		</ng-template>
	`,
    styles: [
        `
			.table-container {
				border: 0.06rem solid grey;
				padding: 1rem;
				margin: 1rem 0;
				border-radius: 0.5rem;
			}
			.center-content {
				display: flex;
				justify-content: space-between;
				align-items: center;
				padding-bottom: 1rem;
			}
			.w-25 {
				width: 25%;
			}
			.auto-size {
				width: auto;
			}
			.mat-checkbox {
				margin-bottom: 0;
			}
		`,
    ],
    standalone: true,
    imports: [
        MatCheckboxModule,
        FormsModule,
        MatButtonModule,
        MatIconModule,
        TableComponent,
        MatFormFieldModule,
        MatInputModule,
        DecimalFormatDirective,
        NumbersOnlyDirective,
        ReactiveFormsModule,
    ],
})
export class PrGradingTableComponent extends FieldType<FieldTypeConfig> implements OnInit {
	@ViewChild('min') min: TemplateRef<any>;
	@ViewChild('amount') amount: TemplateRef<any>;
	@ViewChild('percentage') percentage: TemplateRef<any>;
	@ViewChild('delete') delete: TemplateRef<any>;
	@Output() byFiscalYear = new EventEmitter<boolean>();
	@Output() addNewDeductionGradeEvent = new EventEmitter<DeductionGradingEntity>();
	@Output() deleteDeductionGradeEvent = new EventEmitter<DeductionGradingEntity>();
	@Input() deductionData: DeductionEntity = null;
	@Input() modalGrading = false;
	@Input() employer = false;
	gradingByFy = false;
	deductionGrade: DeductionGradingEntity[] = [];
	newRow: DeductionGradingEntity;
	config: TableConfig<DeductionGradingEntity> = {
		columns: [
			{
				header: 'Min. YTD Earning',
				key: 'MinimumEarnings',
				template: (): TemplateRef<any> => this.min,
			},
			{
				header: 'Amount',
				key: 'Amount',
				template: (): TemplateRef<any> => this.amount,
			},
			{
				header: 'Percentage',
				key: 'Percentage',
				template: (): TemplateRef<any> => this.percentage,
			},
			{
				header: '',
				key: 'IsDelete',
				template: (): TemplateRef<any> => this.delete,
			},
		],
		editActions: {
			idProperty: 'Id',
		},
		defaultSortDirection: 'desc',
		excludePaginator: true,
		editableRows: false,
		defaultSortField: 'MinimumEarnings',
		sortingDisabled: true,
		defaultPageSize: 9999,
	};
	idCounter = -1;
	limitTo = {
		amount: '99999999.99',
		percentage: '100.00',
	};

	public ngOnInit(): void {
		if (this.modalGrading) {
			this.deductionGrade = this.deductionData.PR_DeductionGrade.filter(
				(element: DeductionGradingEntity) => element.Employer === this.employer
			);
			this.gradingByFy = this.deductionData.GradingByFY;
		} else {
			this.gradingByFy = this.model.GradingByFY;
			if (this.props.label === DeductionDetailSection.Employer) {
				this.deductionGrade = this.formControl.value.filter((element: DeductionGradingEntity) => element.Employer);
			} else {
				this.deductionGrade = this.formControl.value.filter((element: DeductionGradingEntity) => !element.Employer);
			}
		}
	}

	searchUnsaveData(): void {
		this.deductionGrade.forEach((element: DeductionGradingEntity) => {
			if (element.Id <= this.idCounter) {
				this.idCounter = element.Id - 1;
			}
		});
	}

	addNewDeductionGrade(): void {
		this.searchUnsaveData();
		const newG: DeductionGradingEntity = {
			Id: this.idCounter,
			DeductionId: this.modalGrading ? this.deductionData.Id : this.options.formState.id,
			Employer: this.modalGrading ? this.employer : this.props.label === DeductionDetailSection.Employer,
			MinimumEarnings: 0,
			Amount: 0,
			Percentage: 0,
		};
		this.deductionGrade = [...this.deductionGrade, newG];
		this.newRow = newG;
		if (!this.modalGrading) {
			this.formControl.setValue([newG, ...this.formControl.value]);
			this.formControl.markAsDirty();
		} else {
			this.addNewDeductionGradeEvent.emit(newG);
		}
		this.idCounter--;
	}

	deleteGrade(grade: DeductionGradingEntity) {
		this.deductionGrade = [...this.deductionGrade.filter((element: DeductionGradingEntity) => element !== grade)];
		if (!this.modalGrading) {
			const newFormValue = [...this.formControl.value.filter((element: DeductionGradingEntity) => element !== grade)];
			this.formControl.setValue(newFormValue);
			this.formControl.markAsDirty();
		} else {
			this.deleteDeductionGradeEvent.emit(grade);
		}
	}

	onGradingByFY() {
		if (this.modalGrading) {
			this.byFiscalYear.emit(this.gradingByFy);
		} else {
			this.props.changeFn();
		}
	}

	newValue(): void {
		if (!this.modalGrading) {
			this.model.PR_DeductionGrade = this.formControl.value.slice();
			this.formControl.markAsDirty();
		}
	}

	preventPaste(event: ClipboardEvent, fc: UntypedFormControl, column, row): void {
		fc.setValue(0);
		row[column.key] = 0;
		event.preventDefault();
	}
}
